import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { CHECK_AUTH, LOGIN, REGISTER, LOGOUT, VERIFY_AGE, VERIFY_EMAIL, FORGOT_PASSWORD, CHECK_PASSCODE, CHANGE_PASSWORD, SOCIAL_AUTH } from '../graphql/queries';
import { useAppDispatch } from '@/store';
import { setUser, clearAuthState } from '../authSlice';
import { LoginCredentials, RegisterCredentials, GraphQLErrorClass } from '../types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';

export const useCheckAuth = () => {
  const dispatch = useAppDispatch();
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  return useQuery(CHECK_AUTH, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.me) {
        // User is authenticated
        dispatch(setUser(data.me));
      } else {
        // User is not authenticated
        dispatch(clearAuthState());
      }
    },
    onError: (error) => {
      // Check if it's an authentication error
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case GraphQLErrorClass.UNAUTHENTICATED:
            dispatch(clearAuthState());
            return;
            break;
        }
      }
      
      // For any other error, show a toast message
      toast.error(tErrors('graphql.default'));
      dispatch(clearAuthState());
    },
  });
};

export const useLogin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  const [loginMutation, { loading, error }] = useMutation(LOGIN, {
    onCompleted: (data) => {
      // Clear any existing toast messages
      toast.dismiss();
      
      const { user } = data.login;
      
      // Store user in Redux
      dispatch(setUser(user));
      
      // Navigate to main app screen
      navigate('/discover');
    },
    onError: (error) => {
      // Check if it's an authentication error
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case GraphQLErrorClass.INVALID_CREDENTIALS:
            // Show the error message from the API
            toast.error(err.message);
            return;
            
          case GraphQLErrorClass.UNAUTHENTICATED:
            dispatch(clearAuthState());
            return;
            break;
        }
      }
      
      // For any other error, show a toast message
      toast.error(tErrors('graphql.default'));
    },
  });
  
  const login = async (credentials: LoginCredentials) => {
    try {
      await loginMutation({
        variables: credentials,
      });
      return true;
    } catch (error) {
      // We've already handled the error in onError callback
      // Just return false without doing anything else
      return false;
    }
  };
  
  return { login, loading, error };
};

export const useRegister = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const [registerMutation, { loading, error }] = useMutation(REGISTER, {
    onCompleted: (data) => {
      // Clear any existing toast messages
      toast.dismiss();
      
      const { user } = data.signup;
      dispatch(setUser(user));
      
      // Navigate to main app screen
      navigate('/discover');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  
  const register = async (credentials: RegisterCredentials) => {
    const { confirmPassword, ...registerData } = credentials;
    
    try {
      await registerMutation({
        variables: registerData,
      });
      return true;
    } catch (error) {
      return false;
    }
  };
  
  return { register, loading, error };
};

export const useLogout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  const [logoutMutation, { loading }] = useMutation(LOGOUT, {
    onCompleted: () => {
      // Clear any existing toast messages
      toast.dismiss();
      
      // Clear auth state on successful logout
      dispatch(clearAuthState());
      
      // Navigate to welcome page
      navigate('/');
    },
    onError: (error) => {
      // Show error message
      toast.error(tErrors('auth.logoutFailed'));
      console.error('Logout error:', error);
      
      // Still clear auth state locally even if the API call fails
      dispatch(clearAuthState());
      navigate('/');
    }
  });
  
  const logout = async () => {
    try {
      await logoutMutation();
    } catch (error) {
      // Error already handled in the onError callback
      console.error('Logout error:', error);
    }
  };
  
  return { logout, loading };
};

export const useVerifyAge = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);

  const [verifyAge, { loading, error, data }] = useLazyQuery(VERIFY_AGE, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      // Check if it's an age verification error
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case GraphQLErrorClass.AGE_VERIFICATION:
            // Show the error message from the API for age verification
            toast.error(err.message);
            return;
            
          case GraphQLErrorClass.VALIDATION:
            toast.error(err.message || tErrors('validation.invalidDateFormat'));
            return;
        }
      }
      
      // For any other error, show a generic message
      toast.error(tErrors('validation.ageVerificationFailed'));
      console.error('Age verification error:', error);
    }
  });

  const checkAge = async (dateOfBirth: string) => {
    try {
      const result = await verifyAge({ 
        variables: { dateOfBirth } 
      });
      
      return result?.data?.verifyAge?.verificationPassed || false;
    } catch (error) {
      // Error already handled in onError callback
      return false;
    }
  };

  return { checkAge, loading, error, verificationPassed: data?.verifyAge?.verificationPassed };
};

export const useVerifyEmail = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);

  const [verifyEmail, { loading, error, data }] = useLazyQuery(VERIFY_EMAIL, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      // Check if it's an email verification error
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case GraphQLErrorClass.EMAIL_VERIFICATION:
            // Show the error message from the API for email verification
            toast.error(err.message);
            return;
            
          case GraphQLErrorClass.VALIDATION:
            toast.error(err.message || tErrors('validation.email'));
            return;
        }
      }
      
      // For any other error, show a generic message
      toast.error('Email verification failed');
      console.error('Email verification error:', error);
    }
  });

  const checkEmail = async (email: string) => {
    try {
      const result = await verifyEmail({ 
        variables: { email } 
      });
      
      return result?.data?.verifyEmail?.available || false;
    } catch (error) {
      // Error already handled in onError callback
      return false;
    }
  };

  return { checkEmail, loading, error, emailAvailable: data?.verifyEmail?.available };
};

export const useForgotPassword = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);

  const [forgotPasswordMutation, { loading, error }] = useMutation(FORGOT_PASSWORD, {
    onError: (error) => {
      toast.error(error.message || tErrors('auth.emailNotFound'));
    }
  });

  const requestPasswordReset = async (email: string) => {
    try {
      const result = await forgotPasswordMutation({
        variables: { email }
      });
      
      return result?.data?.forgotPassword?.success || false;
    } catch (error) {
      return false;
    }
  };

  return { requestPasswordReset, loading, error };
};

export const useCheckPasscode = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);

  const [checkPasscode, { loading, error, data }] = useLazyQuery(CHECK_PASSCODE, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      toast.error(error.message || tErrors('auth.invalidCode'));
    }
  });

  const verifyCode = async (email: string, code: string) => {
    try {
      const result = await checkPasscode({
        variables: { email, code }
      });
      
      return result?.data?.checkForgottenPasscode?.token || null;
    } catch (error) {
      return null;
    }
  };

  return { verifyCode, loading, error, token: data?.checkForgottenPasscode?.token };
};

export const useChangePassword = () => {
  const navigate = useNavigate();
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  const { t } = useTranslation(NAMESPACES.AUTH);

  const [changePasswordMutation, { loading, error }] = useMutation(CHANGE_PASSWORD, {
    onCompleted: (data) => {
      if (data?.changeForgottenPassword?.success) {
        toast.success(t('forgotPassword.successReset') || 'Password reset successfully');
        navigate('/login');
      }
    },
    onError: (error) => {
      toast.error(error.message || tErrors('auth.passwordResetFailed'));
    }
  });

  const resetPassword = async (token: string, password: string) => {
    try {
      const result = await changePasswordMutation({
        variables: { token, password }
      });
      
      return result?.data?.changeForgottenPassword?.success || false;
    } catch (error) {
      return false;
    }
  };

  return { resetPassword, loading, error };
};

export const useSocialAuth = () => {
  const dispatch = useAppDispatch();
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  const [socialAuthMutation, { loading, error }] = useMutation(SOCIAL_AUTH, {
    onCompleted: (data) => {
      // Clear any existing toast messages
      toast.dismiss();
      
      const { user } = data.socialAuth;
      
      // Store user in Redux
      dispatch(setUser(user));
      
      // Note: Callback component will handle navigation
    },
    onError: (error) => {
      // Check if it's an authentication error
      const graphQLErrors = error.graphQLErrors || [];
      console.error('Social auth error:', error);
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case GraphQLErrorClass.UNAUTHENTICATED:
            toast.error(err.message);
            dispatch(clearAuthState());
            return;
        }
      }
      
      // For any other error, show a toast message
      toast.error(tErrors('auth.socialAuthFailed'));
      
      // Note: Callback component will handle navigation
    },
  });
  
  const handleGoogleCallback = async (urlSearchParams: URLSearchParams) => {
    try {
      const code = urlSearchParams.get('code');
      const state = urlSearchParams.get('state');
      
      if (!code) {
        toast.error(tErrors('auth.missingAuthParams'));
        return false;
      }
      
      const result = await socialAuthMutation({
        variables: {
          provider: 'google',
          code,
          state
        },
      });
      
      return !!result.data?.socialAuth?.user;
    } catch (error) {
      console.error('Google auth error:', error);
      // Let the error be caught by the component
      throw error;
    }
  };
  
  const handleAppleCallback = async (urlSearchParams: URLSearchParams) => {
    try {
      const code = urlSearchParams.get('code');
      const state = urlSearchParams.get('state');
      
      if (!code) {
        toast.error(tErrors('auth.missingAuthParams'));
        return false;
      }
      
      const result = await socialAuthMutation({
        variables: {
          provider: 'apple',
          code,
          state
        },
      });
      
      return !!result.data?.socialAuth?.user;
    } catch (error) {
      console.error('Apple auth error:', error);
      // Let the error be caught by the component
      throw error;
    }
  };
  
  return { handleGoogleCallback, handleAppleCallback, loading, error };
}; 